import { Suspense, useMemo } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Text, Grid } from "@react-three/drei";

const S = 0.11; // normalized-unit -> world scale
const WALL_T = 0.06;

function Room({ room, wallH }) {
  const cx = (Number(room.x) + Number(room.w) / 2 - 50) * S;
  const cz = (Number(room.y) + Number(room.h) / 2 - 50) * S;
  const w = Math.max(2, Number(room.w)) * S;
  const d = Math.max(2, Number(room.h)) * S;
  const x0 = (Number(room.x) - 50) * S;
  const z0 = (Number(room.y) - 50) * S;
  const color = room.color || "#3B82F6";

  return (
    <group>
      {/* floor */}
      <mesh position={[cx, 0.01, cz]} receiveShadow>
        <boxGeometry args={[w, 0.04, d]} />
        <meshStandardMaterial color={color} opacity={0.35} transparent />
      </mesh>
      {/* walls */}
      <mesh position={[cx, wallH / 2, z0]}>
        <boxGeometry args={[w, wallH, WALL_T]} />
        <meshStandardMaterial color={color} />
      </mesh>
      <mesh position={[cx, wallH / 2, z0 + d]}>
        <boxGeometry args={[w, wallH, WALL_T]} />
        <meshStandardMaterial color={color} />
      </mesh>
      <mesh position={[x0, wallH / 2, cz]}>
        <boxGeometry args={[WALL_T, wallH, d]} />
        <meshStandardMaterial color={color} />
      </mesh>
      <mesh position={[x0 + w, wallH / 2, cz]}>
        <boxGeometry args={[WALL_T, wallH, d]} />
        <meshStandardMaterial color={color} />
      </mesh>
      <Text position={[cx, wallH + 0.12, cz]} fontSize={0.16} color="#F8FAFC" anchorX="center" anchorY="middle">
        {room.name}
      </Text>
    </group>
  );
}

export default function ThreeDModel({ floorplan, height = 460 }) {
  const rooms = floorplan?.rooms || [];
  const wallH = useMemo(() => Math.max(0.4, (Number(floorplan?.wall_height_ft) || 9) * 0.05), [floorplan]);
  const palette = ["#FDE047", "#3B82F6", "#34D399", "#F472B6", "#FB923C", "#A78BFA"];

  return (
    <div style={{ height }} className="w-full overflow-hidden border border-border bg-[#050a16]" data-testid="threed-model">
      <Canvas shadows camera={{ position: [7, 7, 9], fov: 45 }}>
        <color attach="background" args={["#050a16"]} />
        <ambientLight intensity={0.7} />
        <directionalLight position={[8, 12, 6]} intensity={1.1} castShadow />
        <directionalLight position={[-6, 6, -4]} intensity={0.4} />
        <Suspense fallback={null}>
          <Grid args={[24, 24]} cellSize={0.5} cellColor="#1e293b" sectionColor="#334155" fadeDistance={26} infiniteGrid position={[0, 0, 0]} />
          {rooms.map((r, i) => (
            <Room key={i} room={{ ...r, color: r.color || palette[i % palette.length] }} wallH={wallH} />
          ))}
        </Suspense>
        <OrbitControls enablePan enableZoom enableRotate makeDefault />
      </Canvas>
    </div>
  );
}
